import { REGISTRATION_FORM_DEFINITION_ID } from '@/constants/constants';
import { GetFieldLookupItem, useFormLookups, useFormMetadata } from '@ngt/forms';
import { useScopedField } from '@ngt/forms-core';
import { useEffect, useState } from 'react';

interface UsePostalAddressSyncResult {
    sameAsAddress: boolean;
    sameStateAsAddress: boolean;
}

/**
 * Syncs postal address fields with the main address fields when `addressSame` is checked.
 * Returns flags indicating whether the postal address and/or state are currently mirroring
 * the main address, which can be used to disable the corresponding input fields.
 */
const usePostalAddressSync = (): UsePostalAddressSyncResult => {
    const {state: {value: addressSame}} = useScopedField<boolean>("addressSame", {value: true});
    const {state: {value: address}} = useScopedField<string>("address", {value: true});
    const {state: {value: suburb}} = useScopedField<string>("suburb", {value: true});
    const {state: {value: postcode}} = useScopedField<string>("postcode", {value: true});
    const {state: {value: country}} = useScopedField<string>("country", {value: true});
    const {state: {value: state}} = useScopedField<number>("state", {value: true});

    const {state: {value: postalStreet}, context: {setValue: setPostalStreet}} = useScopedField<string>("postalStreet", {value: true});
    const {state: {value: postalSuburb}, context: {setValue: setPostalSuburb}} = useScopedField<string>("postalSuburb", {value: true});
    const {state: {value: postalState}, context: {setValue: setPostalState}} = useScopedField<string>("postalState", {value: true});
    const {state: {value: postalPostcode}, context: {setValue: setPostalPostcode}} = useScopedField<string>("postalPostcode", {value: true});
    const {state: {value: postalCountry}, context: {setValue: setPostalCountry}} = useScopedField<string>("postalCountry", {value: true});

    const formMetadata = useFormMetadata(REGISTRATION_FORM_DEFINITION_ID);
    const {data: lookups} = useFormLookups(formMetadata, {revalidateOnFocus: false, refreshWhenOffline: false});

    const [sameAsAddress, setSameAsAddress] = useState<boolean>(false);
    const [sameStateAsAddress, setStateSameAsAddress] = useState<boolean>(false);

    useEffect(() => {
        if (addressSame) {
            setPostalStreet(address);
            setPostalSuburb(suburb);
            setPostalPostcode(postcode);
            setPostalCountry(country);

            if (state) {
                setPostalState(GetFieldLookupItem(lookups, 'state', state)?.value?.toString());
                setStateSameAsAddress(true);
            }
            else {
                setStateSameAsAddress(false);
            }

            setSameAsAddress(true);
        }
        else {
            setSameAsAddress(false);
            setStateSameAsAddress(false);
        }
    }, [addressSame, address, postalStreet, suburb, postalSuburb, state, postalState, postalPostcode, postcode, postalCountry, country, lookups]);

    return {sameAsAddress, sameStateAsAddress};
};

export default usePostalAddressSync;
